import React, { useEffect, useState } from "react";
import { Divider } from "primereact/divider";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import { FileUpload } from "primereact/fileupload";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";

import MapDialog from "./MapDialog";

const FormPreview = ({ formName, formDescription, fields }) => {
  const [groupedFields, setGroupedFields] = useState({});
  const [mapDialogVisible, setMapDialogVisible] = useState(false);
  const [selectedMapField, setSelectedMapField] = useState(null);
  const [selectedCoordinates, setSelectedCoordinates] = useState({});

  const handleOpenMapDialog = (field) => {
    setSelectedMapField(field);
    setMapDialogVisible(true);
  };

  const handleSaveCoordinates = (coords) => {
    setSelectedCoordinates((prev) => ({
      ...prev,
      [selectedMapField.name]: coords,
    }));
  };

  useEffect(() => {
    const groupFields = () => {
      const grouped = {};
      fields.forEach((field) => {
        const group = field.group || "ungrouped";
        if (!grouped[group]) {
          grouped[group] = [];
        }
        grouped[group].push(field);
      });
      setGroupedFields(grouped);
    };

    groupFields();
  }, [fields]);

  const renderField = (field) => {
    switch (field.type) {
      case "text":
      case "email":
      case "number":
        return (
          <InputText
            type={field.type}
            maxLength={field.maxLength || undefined}
            min={field.min || undefined}
            max={field.max || undefined}
            placeholder={field.key}
            className="w-full mb-3"
          />
        );
      case "checkbox":
        return field.options.map((option, idx) => (
          <div key={idx} className="p-field-checkbox mb-2">
            <Checkbox
              inputId={`field-${field.name}-option-${idx}`}
              name={field.name}
              value={option.value}
              className="mr-2 ml-5"
            />
            <label
              htmlFor={`field-${field.name}-option-${idx}`}
              className="font-medium"
            >
              {option.option}
            </label>
          </div>
        ));
      case "dropdown":
        return (
          <Dropdown
            value={field.value}
            options={field.options.map((option) => ({
              label: option.option,
              value: option.value,
            }))}
            placeholder={`Select ${field.name}`}
            className="w-full mb-5"
            filter
          />
        );
      case "radio":
        return field.options.map((option, idx) => (
          <div key={idx} className="p-field-radiobutton">
            <RadioButton
              inputId={`field-${field.name}-option-${idx}`}
              name={field.name}
              value={option.value}
              className="mr-2 mb-1 ml-5"
            />
            <label
              htmlFor={`field-${field.name}-option-${idx}`}
              className="font-medium"
            >
              {option.option}
            </label>
          </div>
        ));
      case "date":
        return (
          <Calendar
            placeholder={`Select ${field.name}`}
            className="w-full mb-3"
          />
        );

      case "geolocation":
        return (
          <>
            <Button
              label={
                selectedCoordinates[field.name]
                  ? `Selected: ${selectedCoordinates[field.name][0]}, ${
                      selectedCoordinates[field.name][1]
                    }`
                  : "Select Coordinates"
              }
              icon="pi pi-map-marker"
              onClick={() => handleOpenMapDialog(field)}
              className="w-full mb-3"
              style={{
                backgroundColor: "#609856",
                color: "#fff",
                border: "none",
              }}
            />
          </>
        );

      case "multiselect":
        return (
          <MultiSelect
            value={field.value || []}
            options={(field.options || []).map((o) => ({
              label: o.option,
              value: o.value,
            }))}
            placeholder={field.key}
            className="w-full mb-3"
            display="chip"
          />
        );
      case "inputswitch":
        return (
          <InputSwitch
            placeholder={`Select ${field.name}`}
            className="mb-3 ml-5"
          />
        );
      case "fileupload":
        return (
          <FileUpload
            name={field.name}
            accept={field.fileType === "all" ? "*" : `${field.fileType}/*`}
            className="w-full mb-3 ml-5"
            mode="basic"
            chooseLabel="Choose"
            chooseOptions={{
              className: "p-button p-component",
              style: {
                backgroundColor: "#e29b45",
                color: "#fff",
                border: "none",
              },
            }}
          />
        );
      case "textarea":
        return (
          <InputTextarea
            name={field.name}
            required={field.required}
            className="w-full mb-3"
            mode="basic"
            autoResize
            rows={5}
            cols={30}
          />
        );
      default:
        return null;
    }
  };

  const renderGroupedFields = () => {
    return Object.entries(groupedFields).map(([group, fields]) => {
      const fieldsPerRow = fields[0].fieldsPerRow || 1;
      const flexBasis = `calc(${100 / fieldsPerRow}% - 1rem)`;

      // Check if any field in the group has renderHeader set to true
      const shouldRenderHeader = fields.some(
        (field) => field.renderHeader === true
      );

      return (
        <React.Fragment key={group}>
          {group !== "ungrouped" && shouldRenderHeader && (
            <h3 style={{ width: "100%", marginBottom: "1rem" }}>{group}</h3>
          )}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              marginBottom: "1rem",
            }}
          >
            {fields.map((field, index) => (
              <div
                key={index}
                style={{
                  flex: group === "ungrouped" ? "1 1 100%" : `1 1 ${flexBasis}`,
                  minWidth: group === "ungrouped" ? "100%" : "200px",
                }}
              >
                <label className="block text-900 font-bold mb-2 mt-3">
                  {field.key}
                </label>
                {renderField(field)}
              </div>
            ))}
          </div>
          {group !== "ungrouped" && <Divider />}
        </React.Fragment>
      );
    });
  };
  return (
    <div className="p-grid p-dir-col table-page">
      <div className="p-col title">
        <h1>{formName}</h1>
        <div className="title-bar"></div>
      </div>
      <div className="table-container">
        <ReactMarkdown rehypePlugins={[rehypeRaw]}>
          {formDescription}
        </ReactMarkdown>
        <Divider />
        {renderGroupedFields()}
      </div>
      {mapDialogVisible && (
        <MapDialog
          visible={mapDialogVisible}
          onHide={() => setMapDialogVisible(false)}
          onSave={handleSaveCoordinates}
          initialValue={selectedCoordinates[selectedMapField?.name]}
        />
      )}
    </div>
  );
};

export default FormPreview;
